import { array, codec, optional, nullish, object, union, url } from "zod";
import { NonEmptyString } from "./non-empty-string.js";
/** @import { output } from "zod" */

export const ObjectType = NonEmptyString;
/** @typedef {output<typeof ObjectType>} ObjectType */

export const ImageURL = url();
/** @typedef {output<typeof ImageURL>} ImageURL */

export const Image = object({
  /** A description of what is in the image (not a caption) */
  alt: optional(NonEmptyString),
  secureURL: optional(ImageURL),
  src: ImageURL,
  type: optional(NonEmptyString),
});
/** @typedef {output<typeof Image>} Image */

const OpenGraphObjectMeta = object({
  description: optional(NonEmptyString),
  image: optional(union([Image, array(Image)])),

  /**
   * The title of your object as it should appear within the graph
   *
   * @property {og:title}
   * @example "The Rock"
   */
  title: optional(NonEmptyString),

  /**
   * The type of your object
   *
   * Depending on the type you specify, other properties may also be required.
   *
   * @example "video.movie"
   * @see {@link ObjectType}
   */
  type: optional(ObjectType),
  url: optional(url()),
});
/** @typedef {output<typeof OpenGraphObjectMeta>} OpenGraphObjectMeta */

export const Meta = codec(
  object({
    ...OpenGraphObjectMeta.shape,
    description: nullish(OpenGraphObjectMeta.shape.description.unwrap()),
    image: nullish(union([union([ImageURL, Image]), array(union([ImageURL, Image]))])),
    title: nullish(OpenGraphObjectMeta.shape.title.unwrap()),
    type: nullish(ObjectType),
    url: nullish(url()),
  }),
  OpenGraphObjectMeta,
  {
    decode: (input) => ({
      ...input,
      description: input.description ?? undefined,
      image:
        typeof input.image === "string"
          ? { src: input.image }
          : Array.isArray(input.image)
            ? input.image.map((i) => (typeof i === "string" ? { src: i } : i))
            : (input.image ?? undefined),
      title: input.title ?? undefined,
      type: input.type ?? undefined,
      url: input.url ?? undefined,
    }),
    encode: (output) => output,
  },
);
/** @typedef {output<typeof Meta>} Meta */

/**
 *
 * @param {string} name
 * @returns {name is `og:${string}`}
 */
export function isProperty(name) {
  return name.startsWith("og:");
}
